import { useParams } from "react-router-dom";
import { Loader, PageLayout } from "../../shared/ui";
import { useIsMobile } from "../../shared/hooks/useIsMobile";
import { PhaseActionBar } from "../../features/work-session";
import {
  TechnicianFeedbackCard,
  useAppointmentTechnicianFeedback,
} from "../../features/technician-feedback";
import { hasTechnicianFeedback } from "../../shared/lib/technicianFeedback";
import { useAppointmentFlows } from "./model/useAppointmentFlows";
import { useWorkerAppointment } from "./model/useWorkerAppointment";
import { AppointmentActions } from "./ui/AppointmentActions";
import { AppointmentDetailFields } from "./ui/AppointmentDetailFields";
import { AppointmentFlowDialogs } from "./ui/AppointmentFlowDialogs";
import { AppointmentSecondaryActions } from "./ui/AppointmentSecondaryActions";

export const WorkerAppointmentPage = () => {
  const { appointmentId: appointmentIdParam } = useParams<{ appointmentId: string }>();
  // Route params are strings; Appointment.id is a number since the ScheduleEntry refactor.
  const appointmentId = appointmentIdParam ? Number(appointmentIdParam) : undefined;
  const isMobile = useIsMobile();
  const { isLoading, isError, taps, vm } = useWorkerAppointment(appointmentId);
  const flows = useAppointmentFlows({ taps, workOrderId: vm?.appointment.workOrderId });
  // Above the early returns, like every other hook here — takes `null` while loading.
  const feedback = useAppointmentTechnicianFeedback(vm?.appointment ?? null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (isError || !vm) {
    return (
      <div className="flex items-center justify-center h-full text-red-400">
        Termin konnte nicht geladen werden.
      </div>
    );
  }

  // Same decision, one bar: the desktop copy sits inline in PageLayout's header row, the mobile
  // copy pins to the bottom above the safe area. `AppointmentSecondaryActions` is the single place
  // "Umplanen" / "Folgetermin erforderlich" are decided either way — see D5 in the review.
  const desktopActionBar = vm.showActionButtons && vm.primaryAction ? (
    <>
      <AppointmentSecondaryActions
        appearance="header"
        phase={vm.phase}
        onFollowUp={flows.onFollowUp}
        onReschedule={flows.onReschedule}
      />
      <PhaseActionBar
        action={vm.primaryAction}
        elapsedSince={vm.elapsedSince}
        isPending={taps.isPending}
        failed={taps.failed}
        confirmedAt={taps.confirmedAt}
        onAction={flows.onPrimaryAction}
        onUndo={taps.undo}
      />
    </>
  ) : undefined;

  const mobileActionBar = vm.showActionButtons ? (
    <AppointmentActions
      action={vm.primaryAction}
      phase={vm.phase}
      elapsedSince={vm.elapsedSince}
      isPending={taps.isPending}
      failed={taps.failed}
      confirmedAt={taps.confirmedAt}
      onAction={flows.onPrimaryAction}
      onUndo={taps.undo}
      onFollowUp={flows.onFollowUp}
      onReschedule={flows.onReschedule}
    />
  ) : undefined;

  return (
    <>
      <PageLayout
        pageTitle={`Reparaturauftrag ${vm.title}`}
        back
        headerChildren={isMobile ? undefined : desktopActionBar}
        bottomBar={isMobile ? mobileActionBar : undefined}
      >
        {/* `AppointmentActions` is `position: fixed` on mobile (a floating card, not a flex
            sibling), so nothing here reserves room for it automatically any more — this bottom
            padding is that reservation, restated locally rather than in shared `PageLayout` CSS.
            Generous on purpose: the bar's own height plus a device's safe-area inset both vary,
            and unused scroll space costs nothing while a hidden card does. */}
        <div className={`space-y-3 ${isMobile && mobileActionBar ? "pb-32" : ""}`}>
          <AppointmentDetailFields
            appointment={vm.appointment}
            ext={vm.ext}
            mapsQuery={vm.mapsQuery}
            phase={vm.phase}
            statusLabel={vm.statusLabel}
            statusColor={vm.statusColor}
            previousAppointments={vm.previousAppointments}
            onDiscardTravel={taps.undo}
          />
          {/* Comment and files back to the office: from the moment the job starts and for as long
              as the page exists afterwards — and earlier only when something was already sent. */}
          {feedback && (vm.phase !== null || hasTechnicianFeedback(vm.appointment)) && (
            <TechnicianFeedbackCard controller={feedback} />
          )}
        </div>
      </PageLayout>
      <AppointmentFlowDialogs appointment={vm.appointment} technicianId={vm.technicianId} flows={flows} />
    </>
  );
};
